import React, { useCallback, useState, useMemo, useEffect } from "react";
import { IMedicalQuestionProps } from "../../../models";
import { FastField, FieldProps, getIn } from "formik";
import RequiredIndicator from "../../../../RequiredIndicator";
import  useFormContext from "../../../FormContext";
import { ButtonGroup, Button, Intent, TextArea } from "@blueprintjs/core";
import { hasRequiredValidation } from '../../../models/validation';

type Props<T = any> = FieldProps<T> & { fieldProps: IMedicalQuestionProps, className: string };

const BoundMedQuestionField = ({ field, form, fieldProps }: Props) => {

  const { isEditing } = useFormContext();
  const isRequired = hasRequiredValidation(fieldProps.validations);
  const { requiresNoteIfYes, questionId } = fieldProps;

  const value = useMemo(() => field.value || { "QuestionId": questionId, "NoteOnYes": requiresNoteIfYes }, [field.value, questionId, requiresNoteIfYes])

  useEffect(() => {
    if(isEditing && !field.value)
      form.setFieldValue(field.name, { ...value });
  }, [field.name, field.value, form, isEditing, value]);

  const showError = isEditing && (form.submitCount || getIn(form.touched, field.name)) && getIn(form.errors, field.name);
  const [tempNote, setTempNote] = useState(value.setNote);

  const removeNote = useCallback(() => {
    if (requiresNoteIfYes && value.Note) {
      delete value.Note;
      setTempNote(value.setNote);
      form.setFieldValue(field.name, { ...value});
    }
  }, [field.name, form, requiresNoteIfYes, value]);

  const setAnswerYes = useCallback(() => {
    value.Answer = true;
    form.setFieldValue(field.name, { ...value, Answer: true });
    form.setFieldTouched(field.name, true);
  }, [field.name, form, value]);

  const setAnswerNo = useCallback(() => {
    value.Answer = false;
    form.setFieldValue(field.name, { ...value, Answer: false });
    form.setFieldTouched(field.name, true);
    removeNote();
  }, [field.name, form, value, removeNote]);

  const setNote = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => { setTempNote(e.currentTarget.value); }, []);

  const onBlur = useCallback((e: React.FocusEvent) => {
    value.Note = tempNote;
    form.setFieldValue(field.name, { ...value, Note: tempNote });
    form.setFieldTouched(field.name, true);
  }, [field.name, form, tempNote, value]);

  return (
    <div style={{ paddingTop: 5, paddingBottom: 5 }} >
      <div style={{ display: 'inline-block', width: '85%' }}>{fieldProps.label}<RequiredIndicator show={isRequired} />
      { showError &&
        <span style={{ paddingLeft: '15px', color: '#c23030', fontSize: '12px'}}>{getIn(form.errors, field.name)}</span>
      }
      </div>
      <div style={{ display: 'inline-block' }}>
        <ButtonGroup>
          <Button text="Yes" onClick={setAnswerYes} disabled={!isEditing} intent={value.Answer ? Intent.PRIMARY : Intent.NONE} />
          <Button text="No" onClick={setAnswerNo} disabled={!isEditing} intent={value.Answer === false ? Intent.PRIMARY : Intent.NONE} />
        </ButtonGroup>
      </div>
      { requiresNoteIfYes && value.Answer &&
        <div style={{ paddingTop: 5, paddingBottom: 5 }}>
          <TextArea placeholder={(isEditing && "Please provide further explanation") || undefined} style={{ width: '50%' }} disabled={!isEditing} onChange={setNote}
            value={(isEditing) ? tempNote : value.Note} onBlur={onBlur} /><RequiredIndicator show={true} />
        </div>
      }
    </div>
  )
}

const MedQuestionField = (props: IMedicalQuestionProps) => {
  //const validate = isEditing ? validationMethodFactory(props) : undefined;
  return <FastField name={props.binding} fieldProps={props} component={BoundMedQuestionField} />;
}

export default MedQuestionField;
